import applepayLight from '@noah-labs/fe-shared-ui-assets/svg/images/applepay-light.svg';
import googlepayLight from '@noah-labs/fe-shared-ui-assets/svg/images/googlepay-light.svg';
import { Landmark } from 'lucide-react';
import type { TpPaymentMethodUI } from '../types';
import { TpPaymentMethodType } from '../types';
import { formatAddress } from './formatAddress';
import { getCardSchemeIcon } from './getCardSchemeIcon';
import { isBankPaymentMethod, isCardPaymentMethod, isIdentifierPixMethod } from './paymentMethods';

type TpPaymentMethodInfo = {
  address?: string;
  dataQa?: string;
  primary: string;
  primaryTypographyProps?: { color?: string };
  secondary?: string;
  svg?: TpReactSvg;
};

type TpGetPaymentMethodUI = {
  hideAddress?: boolean;
  paymentMethod?: TpPaymentMethodUI;
  type: TpPaymentMethodType | undefined;
};

export function getPaymentMethodUI({
  hideAddress,
  paymentMethod,
  type,
}: TpGetPaymentMethodUI): TpPaymentMethodInfo {
  switch (type) {
    case TpPaymentMethodType.ApplePay:
      return { dataQa: 'apple-pay', primary: 'Apple Pay', svg: applepayLight };

    case TpPaymentMethodType.BankAch: {
      if (isBankPaymentMethod(paymentMethod)) {
        const bankCode = paymentMethod.bankCode ? `- ${paymentMethod.bankCode}` : '';
        return {
          primary: 'ACH bank transfer',
          secondary: `${paymentMethod.accountNumber} ${bankCode}`,
          svg: Landmark,
        };
      }
      return {
        primary: 'ACH bank transfer',
        secondary: 'Send from a bank account',
        svg: Landmark,
      };
    }

    case TpPaymentMethodType.BankFedwire: {
      if (isBankPaymentMethod(paymentMethod)) {
        const bankCode = paymentMethod.bankCode ? `- ${paymentMethod.bankCode}` : '';
        return {
          primary: 'Wire transfer',
          secondary: `${paymentMethod.accountNumber} ${bankCode}`,
          svg: Landmark,
        };
      }
      return {
        primary: 'Wire transfer',
        secondary: 'Send from a bank account',
        svg: Landmark,
      };
    }

    case TpPaymentMethodType.BankSepa: {
      if (isBankPaymentMethod(paymentMethod)) {
        const bankCode = paymentMethod.bankCode ? `- ${paymentMethod.bankCode}` : '';
        return {
          primary: 'SEPA bank transfer',
          secondary: `${paymentMethod.accountNumber} ${bankCode}`,
          svg: Landmark,
        };
      }
      return {
        primary: 'SEPA bank transfer',
        secondary: 'Send from a bank account',
        svg: Landmark,
      };
    }

    case TpPaymentMethodType.BankTransfer: {
      if (isBankPaymentMethod(paymentMethod)) {
        const bankCode = paymentMethod.bankCode ? `- ${paymentMethod.bankCode}` : '';
        const showAddress = paymentMethod.accountHolderAddress && !hideAddress;
        return {
          address: showAddress ? formatAddress(paymentMethod.accountHolderAddress) : undefined,
          dataQa: 'bank-transfer',
          primary: `${paymentMethod.accountCurrency.code} account`,
          secondary: `${paymentMethod.accountNumber} ${bankCode}`,
          svg: Landmark,
        };
      }
      return { primary: 'Bank transfer', svg: Landmark };
    }

    case TpPaymentMethodType.Card:
      if (isCardPaymentMethod(paymentMethod)) {
        return {
          address: !hideAddress ? formatAddress(paymentMethod.billingAddress) : undefined,
          dataQa: `credit-card-${paymentMethod.last4}`,
          primary: paymentMethod.issuer || 'Credit/Debit card',
          secondary: `**** ${paymentMethod.last4}`,
          svg: getCardSchemeIcon(paymentMethod.scheme),
        };
      }
      return {
        primary: 'Credit/Debit card',
        secondary: 'We accept Visa and Mastercard',
        svg: getCardSchemeIcon(undefined),
      };

    case TpPaymentMethodType.GooglePay:
      return { dataQa: 'google-pay', primary: 'Google Pay', svg: googlepayLight };

    case TpPaymentMethodType.IdentifierPix:
      if (isIdentifierPixMethod(paymentMethod)) {
        return {
          primary: 'Pix',
          secondary: paymentMethod.identifier,
          svg: Landmark,
        };
      }
      return { primary: 'Pix', svg: Landmark };

    default:
      return {
        dataQa: 'choose-payment-method',
        primary: 'Choose a payment method',
        primaryTypographyProps: { color: 'text.brandLink' },
      };
  }
}
